import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  arrayRemove,
  arrayUnion,
  serverTimestamp,
} from "firebase/firestore";
import { db, functions as firebaseFunctions } from "../../firebase";
import { EmailOutlined, PhoneOutlined } from "@mui/icons-material";
import { httpsCallable } from "firebase/functions";
import { getAuth } from "firebase/auth";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  getMetadata,
  uploadString,
  deleteObject,
} from "firebase/storage";
import { roundMoney, roundSize } from "../../utils/money";
import { extractInvoiceIdFromInput } from "../../utils/invoice";
import { refreshOrderPaymentStatus as refreshOrderPaymentStatusCallable } from "../../utils/firebaseFunctions";
import { MaxRectsPacker } from "maxrects-packer";
import { DPI, SHEET_WIDTH_IN, SHEET_HEIGHT_IN } from "../../config/dtfConfig";

import { changeDpiDataUrl } from "changedpi";

// --- helpers for Firebase Storage URLs/paths ---
const isHttpUrl = (u) => /^https?:\/\//i.test(String(u || ""));
const isAllowedImageSrc = (u) => {
  const s = String(u || "").trim();
  return /^data:image\//i.test(s) || /^https?:\/\//i.test(s);
};
const safeImageUrl = (u) => (isAllowedImageSrc(u) ? String(u) : "");
const isAllowedDownloadSrc = (u) => {
  const s = String(u || "").trim();
  return /^data:/i.test(s) || /^https?:\/\//i.test(s);
};
const extractStoragePath = (downloadUrl) => {
  try {
    const s = String(downloadUrl);
    // handle both firebasestorage.googleapis.com and firebasestorage.app
    const m = s.match(/\/(?:o)\/([^?]+)\?/);
    if (!m || !m[1]) return null;
    return decodeURIComponent(m[1]); // e.g. orders/uid/orderId/file.png
  } catch (_) {
    return null;
  }
};

const MM_PER_INCH = 25.4;

const parseMaybeJson = (value) => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      return JSON.parse(trimmed);
    } catch (_) {
      return null;
    }
  }
  return value;
};

const inferNameFromPath = (path) => {
  if (typeof path !== "string" || !path) return "";
  try {
    const withoutQuery = path.split("?")[0];
    const parts = withoutQuery.split("/");
    return parts[parts.length - 1] || "";
  } catch (_) {
    return "";
  }
};

const toFiniteNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const toTitleCase = (value) => {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

const flattenDeep = (inputs) => {
  if (inputs === null || inputs === undefined) return [];
  const queue = Array.isArray(inputs) ? inputs.slice() : [inputs];
  const out = [];
  while (queue.length) {
    const value = queue.shift();
    if (value === null || value === undefined) continue;
    if (Array.isArray(value)) {
      queue.unshift(...value);
    } else {
      out.push(value);
    }
  }
  return out;
};

const normalizePictureLike = (item, idx, context = {}) => {
  if (!item) return null;
  if (typeof item === "string") {
    const parsed = parseMaybeJson(item);
    if (!parsed || typeof parsed === "string") return null;
    return normalizePictureLike(parsed, idx, context);
  }
  if (typeof item !== "object") return null;

  const asset = context.asset;
  const candidateSources = [
    item.file,
    item.url,
    item.source,
    asset?.url,
    asset?.original,
  ];
  const file =
    candidateSources.find((src) => typeof src === "string" && src) || null;
  if (!file) return null;

  const rawQuantity =
    item.quantity ??
    item.count ??
    context.quantity ??
    context.cartItemQuantity ??
    1;
  const quantity = Math.max(1, Number(rawQuantity) || 1);

  const parseDim = (val) => toFiniteNumber(val);
  let widthIn = parseDim(item.widthIn ?? item.width ?? item.width_in);
  let heightIn = parseDim(item.heightIn ?? item.height ?? item.height_in);

  const widthMm =
    parseDim(item?.size?.width) ??
    parseDim(context.size?.width) ??
    parseDim(asset?.size?.width);
  const heightMm =
    parseDim(item?.size?.height) ??
    parseDim(context.size?.height) ??
    parseDim(asset?.size?.height);

  if ((widthIn == null || widthIn <= 0) && widthMm && widthMm > 0) {
    widthIn = roundSize(widthMm / MM_PER_INCH);
  }
  if ((heightIn == null || heightIn <= 0) && heightMm && heightMm > 0) {
    heightIn = roundSize(heightMm / MM_PER_INCH);
  }

  const aspect = parseDim(item.aspect);
  if ((widthIn == null || widthIn <= 0) && aspect && heightIn && heightIn > 0) {
    widthIn = roundSize(aspect * heightIn);
  }
  if ((heightIn == null || heightIn <= 0) && aspect && widthIn && widthIn > 0) {
    heightIn = roundSize(widthIn / aspect);
  }

  if (widthIn == null || widthIn <= 0) widthIn = 1;
  if (heightIn == null || heightIn <= 0) heightIn = 1;

  const viewLabel = item.view || context.view || "";
  const baseName =
    (typeof item.name === "string" && item.name.trim()) ||
    inferNameFromPath(asset?.original) ||
    inferNameFromPath(file) ||
    (context.fallbackNameBase
      ? `${context.fallbackNameBase}`.trim()
      : `design-${(context.designIndex ?? 0) + 1}-${idx + 1}`);
  const name =
    viewLabel && !baseName.toLowerCase().includes(viewLabel.toLowerCase())
      ? `${baseName} (${viewLabel})`
      : baseName;

  return {
    ...item,
    file,
    name,
    quantity,
    widthIn,
    heightIn,
    view: viewLabel || item.view,
    cartItemId: item.cartItemId || context.cartItemId,
    designIndex: item.designIndex ?? context.designIndex,
  };
};

const convertDesignEntry = (entry, designIndex = 0) => {
  if (!entry || typeof entry !== "object") return [];
  const elements = Array.isArray(entry.elements) ? entry.elements : [];
  if (!elements.length) return [];

  const assets = Array.isArray(entry.assets) ? entry.assets : [];
  const assetLookup = new Map();
  assets.forEach((asset) => {
    const url = asset?.url;
    if (url) assetLookup.set(url, asset);
    const original = asset?.original;
    if (original && !assetLookup.has(original)) {
      assetLookup.set(original, asset);
    }
  });

  return elements
    .filter((el) => {
      if (!el) return false;
      if (!el.type) return true;
      return String(el.type).toLowerCase() === "image";
    })
    .map((el, idx) =>
      normalizePictureLike(el, idx, {
        designIndex,
        asset:
          assetLookup.get(el?.source) ||
          assetLookup.get(el?.id) ||
          assets[idx] ||
          null,
        fallbackNameBase: entry.cartItemId,
        cartItemId: entry.cartItemId,
        quantity: entry.quantity ?? entry.count ?? 1,
      })
    )
    .filter(Boolean);
};

const extractDesignImagesFromPayload = (payload) => {
  if (payload === null || payload === undefined) return [];
  const parsed = parseMaybeJson(payload) ?? payload;

  if (Array.isArray(parsed)) {
    const looksLikeDesign = parsed.some(
      (entry) => entry && Array.isArray(entry?.elements)
    );
    if (looksLikeDesign) {
      return parsed.flatMap((entry, index) => convertDesignEntry(entry, index));
    }
    return parsed
      .map((entry, idx) => normalizePictureLike(entry, idx))
      .filter(Boolean);
  }

  if (typeof parsed === "object") {
    if (Array.isArray(parsed.elements)) {
      const hasLegacyFile = parsed.elements.some(
        (it) => it && (it.file || it.url || it.source)
      );
      if (hasLegacyFile) {
        return parsed.elements
          .map((entry, idx) =>
            normalizePictureLike(entry, idx, {
              asset: Array.isArray(parsed.assets)
                ? parsed.assets[idx]
                : undefined,
              fallbackNameBase: parsed.cartItemId,
              cartItemId: parsed.cartItemId,
              quantity: parsed.quantity ?? parsed.count ?? 1,
            })
          )
          .filter(Boolean);
      }
      return convertDesignEntry(parsed, 0);
    }

    if (Array.isArray(parsed.pictures)) {
      return parsed.pictures
        .map((entry, idx) => normalizePictureLike(entry, idx))
        .filter(Boolean);
    }

    if (Array.isArray(parsed.images)) {
      return parsed.images
        .map((entry, idx) => normalizePictureLike(entry, idx))
        .filter(Boolean);
    }
  }

  return [];
};

const collectOrderImageItems = (...payloads) => {
  const flatList = flattenDeep(payloads);
  if (!flatList.length) return [];
  return flatList.flatMap((payload, index) => {
    const normalized = extractDesignImagesFromPayload(payload);
    if (normalized.length) return normalized;
    // If we reach here and the payload is a single object resembling a picture
    if (
      payload &&
      typeof payload === "object" &&
      (payload.file || payload.url || payload.source)
    ) {
      const single = normalizePictureLike(payload, index);
      return single ? [single] : [];
    }
    return [];
  });
};

const collectConceptCanvasEntries = (...payloads) => {
  const flatList = flattenDeep(payloads).map((item) =>
    typeof item === "string" ? (parseMaybeJson(item) ?? item) : item
  );
  if (!flatList.length) return [];

  const buildEntryFromConcept = (concept, index) => {
    if (!concept || typeof concept !== "object") return null;

    const quantity = Math.max(
      1,
      Number(
        concept.quantity ??
          concept.count ??
          concept.minimumQuantity ??
          concept.minQuantity ??
          1
      ) || 1
    );
    const cartItemRaw =
      concept.cartItemId ?? concept.id ?? `concept-${index + 1}`;
    const cartItemId = String(
      cartItemRaw !== undefined && cartItemRaw !== null
        ? cartItemRaw
        : `concept-${Date.now()}-${index}`
    );
    const colorName =
      typeof concept.color === "string"
        ? concept.color
        : concept.color?.name || "";
    const price = concept.color?.price || "";
    const sizeLabel =
      concept.size ||
      concept.shirtSize ||
      concept.garmentSize ||
      concept?.shirt?.size ||
      "";
    const shirtName =
      typeof concept?.shirt === "string"
        ? concept.shirt
        : concept?.shirt?.name || "";

    const productTitle = () => {
      const details = concept.shirt;
      if (!details || !details.name) return "Selected Product";
      let name = details.name.trim();
      // Remove style code: split on last '-' and take the left part
      const dashIdx = name.lastIndexOf("-");
      if (dashIdx !== -1) {
        name = name.substring(0, dashIdx).trim();
      }
      // Remove brand: split on first '·' and take the right part
      const dotIdx = name.indexOf("·");
      if (dotIdx !== -1) {
        name = name.substring(dotIdx + 1).trim();
      }
      return name || "Selected Product";
    };

    const localSeen = new Set();
    const images = [];
    const pushImage = (viewKey, url) => {
      if (typeof url !== "string") return;
      const normalizedUrl = url.trim();
      if (!normalizedUrl) return;
      if (localSeen.has(normalizedUrl)) return;
      localSeen.add(normalizedUrl);

      const viewName =
        toTitleCase(
          viewKey
            .replace(/[_-]+/g, " ")
            .replace(/design url/i, "")
            .trim()
        ) || "Preview";
      images.push({
        url: normalizedUrl,
        view: viewName,
        label: viewName,
      });
    };

    // Prefer with-shirt previews (front first, then back)
    const frontWithShirt = concept.front_design_with_shirt_url;
    const backWithShirt = concept.back_design_with_shirt_url;

    if (frontWithShirt || backWithShirt) {
      // Show front/back with-shirt previews first
      if (frontWithShirt) pushImage("front", frontWithShirt);
      if (backWithShirt) pushImage("back", backWithShirt);

      // Then include other design views (left/right/hood/sleeve) if present
      const extraViews = {
        left: concept.left_design_url,
        right: concept.right_design_url,
        hood: concept.hood_design_url,
        sleeve: concept.sleeve_design_url,
      };
      Object.entries(extraViews).forEach(([key, value]) => pushImage(key, value));
    } else {
      // Fallback to prior explicit views if with-shirt images are missing
      const explicitViews = {
        front: concept.front_design_url,
        back: concept.back_design_url,
        left: concept.left_design_url,
        right: concept.right_design_url,
        hood: concept.hood_design_url,
        sleeve: concept.sleeve_design_url,
      };
      Object.entries(explicitViews).forEach(([key, value]) =>
        pushImage(key, value)
      );
    }

    Object.entries(concept).forEach(([key, value]) => {
      if (typeof value !== "string") return;
      const match = key.match(/([^:_-]+)[_\-:]?design[_\-:]?url$/i);
      if (!match) return;
      const viewKey = match[1];
      pushImage(viewKey, value);
    });

    if (!images.length) {
      const fallbackPictures = extractDesignImagesFromPayload(concept);
      fallbackPictures.forEach((pic, idx) => {
        const candidateUrl = pic?.file || pic?.url || pic?.source;
        if (!candidateUrl) return;
        pushImage(pic?.view || pic?.name || `Image ${idx + 1}`, candidateUrl);
      });
    }

    if (!images.length) return null;

    const primaryUrl = images[0]?.url;
    if (!primaryUrl) return null;

    const viewLabel = images[0]?.view || "Preview";
    const displayName = productTitle();

    // Gather without-shirt download URLs (front/back) if present
    const downloads = [];
    const frontNoShirt = concept.front_design_without_shirt_url || concept.front_design_url;
    const backNoShirt = concept.back_design_without_shirt_url || concept.back_design_url;
    if (frontNoShirt) downloads.push({ url: String(frontNoShirt), label: "Front" });
    if (backNoShirt) downloads.push({ url: String(backNoShirt), label: "Back" });

    return {
      url: primaryUrl,
      images,
      quantity,
      view: viewLabel,
      cartItemId,
      name: displayName,
      color: colorName,
      price: price,
      size: sizeLabel,
      shirtName,
      designDownloads: downloads,
    };
  };

  return flatList
    .map((concept, index) => buildEntryFromConcept(concept, index))
    .filter(Boolean);
};

export default function ShirtlabDetails({
  selectedOrder,
  onBack,
  handleStatusChange,
  currentStatus,
  customerData,
}) {
  const [imageSizes, setImageSizes] = useState([]);
  const [pictureSizes, setPictureSizes] = useState([]);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const [isRefreshingPaymentStatus, setIsRefreshingPaymentStatus] =
    useState(false);
  const [cropToast, setCropToast] = useState("");
  const [isBuildingSheets, setIsBuildingSheets] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [buildUploadedCount, setBuildUploadedCount] = useState(0);
  const [buildUploadTotal, setBuildUploadTotal] = useState(0);
  const [statusOpen, setStatusOpen] = useState(false);
  const [internalNotes, setInternalNotes] = useState(
    Array.isArray(selectedOrder?.internalNotes)
      ? selectedOrder.internalNotes
      : []
  );
  const [newNote, setNewNote] = useState("");

  // Local copy of pictures for live updates when attaching items
  const designImages = useMemo(() => {
    if (!selectedOrder) return [];
    return collectOrderImageItems(
      selectedOrder.designs,
      selectedOrder?.raw?.designs,
      selectedOrder?.designsJson,
      selectedOrder?.designData,
      selectedOrder?.shirtlabDesigns,
      selectedOrder?.pictures,
      selectedOrder?.raw?.pictures
    );
  }, [selectedOrder]);
  const [localDesigns, setLocalDesigns] = useState(designImages);

  useEffect(() => {
    setLocalDesigns(designImages);
  }, [selectedOrder?.id, designImages]);

  const mapSourcesToCanvasEntries = useCallback((sheetGroups, images) => {
    const groupsArray = Array.isArray(sheetGroups) ? sheetGroups : [];
    if (groupsArray.length) {
      return groupsArray.map((g) => ({
        url: typeof g === "string" ? g : g?.url || "",
        quantity: Math.max(
          1,
          Number((typeof g === "object" && (g.quantity ?? g.count)) ?? 1)
        ),
        name: toTitleCase(
          (typeof g === "object" && (g.name || g.label || g.view)) || "Sheet"
        ),
        images: (() => {
          const url = typeof g === "string" ? g : g?.url || "";
          return url ? [{ url, view: "Sheet", label: "Sheet" }] : [];
        })(),
      }));
    }
    const imgsArray = Array.isArray(images) ? images : [];
    return imgsArray.map((it) => ({
      url: typeof it === "string" ? it : it?.url || "",
      quantity: Math.max(
        1,
        Number((typeof it === "object" && (it.quantity ?? it.count)) ?? 1)
      ),
      name: toTitleCase(
        (typeof it === "object" && (it.name || it.label || it.view)) || "Sheet"
      ),
      images: (() => {
        const url = typeof it === "string" ? it : it?.url || "";
        const label = toTitleCase(it?.view || it?.name || "Sheet");
        return url ? [{ url, view: label, label }] : [];
      })(),
    }));
  }, []);

  const conceptEntries = useMemo(
    () =>
      collectConceptCanvasEntries(
        selectedOrder?.items,
        selectedOrder?.raw?.items,
        selectedOrder?.itemsJson,
        selectedOrder?.concepts,
        selectedOrder?.raw?.concepts,
        selectedOrder?.conceptsJson,
        selectedOrder?.conceptData,
        selectedOrder?.shirtlabConcepts,
        selectedOrder?.conceptsPayload
      ),
    [
      selectedOrder?.items,
      selectedOrder?.raw?.items,
      selectedOrder?.itemsJson,
      selectedOrder?.concepts,
      selectedOrder?.raw?.concepts,
      selectedOrder?.conceptsJson,
      selectedOrder?.conceptData,
      selectedOrder?.shirtlabConcepts,
      selectedOrder?.conceptsPayload,
    ]
  );

  const [canvasEntries, setCanvasEntries] = useState(() =>
    conceptEntries.length
      ? conceptEntries
      : mapSourcesToCanvasEntries(
          selectedOrder?.sheetGroups,
          selectedOrder?.images
        )
  );

  useEffect(() => {
    if (conceptEntries.length) {
      setCanvasEntries(conceptEntries);
      return;
    }
    setCanvasEntries(
      mapSourcesToCanvasEntries(
        selectedOrder?.sheetGroups,
        selectedOrder?.images
      )
    );
  }, [
    conceptEntries,
    selectedOrder?.id,
    selectedOrder?.sheetGroups,
    selectedOrder?.images,
    mapSourcesToCanvasEntries,
  ]);

  useEffect(() => {
    setInternalNotes(
      Array.isArray(selectedOrder?.internalNotes)
        ? selectedOrder.internalNotes
        : []
    );
  }, [selectedOrder?.internalNotes]);

  // Close preview with Escape
  useEffect(() => {
    if (!enlargedImage) return;
    const onKey = (e) => {
      if (e.key === "Escape") setEnlargedImage(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enlargedImage]);

  const handleAddInternalNote = async () => {
    const text = newNote.trim();
    if (!text) return;
    try {
      const authUser = getAuth().currentUser;
      const authorId = authUser?.uid || null;
      let staffFirst = "";
      let staffLast = "";
      try {
        if (authorId) {
          const staffSnap = await getDoc(doc(db, "users", authorId));
          if (staffSnap.exists()) {
            const staff = staffSnap.data();
            staffFirst = staff?.firstName || staff?.first_name || "";
            staffLast = staff?.lastName || staff?.last_name || "";
          }
        }
      } catch (innerErr) {
        console.warn("⚠️ Could not fetch staff user doc for notes:", innerErr);
      }
      const fallbackName = authUser?.displayName || authUser?.email || "Staff";
      const authorName = `${staffFirst} ${staffLast}`.trim() || fallbackName;

      const note = {
        text,
        createdAt: new Date().toISOString(),
        authorId,
        authorEmail: authUser?.email || null,
        authorName,
      };

      await updateDoc(doc(db, "orders", selectedOrder.id), {
        internalNotes: arrayUnion(note),
      });
      setInternalNotes((prev) => [...prev, note]);
      setNewNote("");
    } catch (err) {
      console.error("❌ Failed to add internal note:", err);
      alert("Couldn't save note. Check console.");
    }
  };

  // ---- Build sheets from order's pictures (admin action) ----
  async function buildSheetsFromPictures(picturesList = []) {
    // Helper: trim transparent border and compute size ratios
    const trimTransparentBorder = (imgEl, alphaThreshold = 10) => {
      try {
        const w = imgEl.naturalWidth || imgEl.width;
        const h = imgEl.naturalHeight || imgEl.height;
        if (!w || !h) return { changed: false };
        const c = document.createElement("canvas");
        c.width = w;
        c.height = h;
        const x = c.getContext("2d");
        x.drawImage(imgEl, 0, 0);
        const { data } = x.getImageData(0, 0, w, h);
        let top = 0,
          left = 0,
          right = w - 1,
          bottom = h - 1;
        const isOpaqueAt = (px) => data[px + 3] > alphaThreshold;
        for (; top <= bottom; top++) {
          let f = false;
          const row = top * w * 4;
          for (let col = 0; col < w; col++) {
            if (isOpaqueAt(row + col * 4)) {
              f = true;
              break;
            }
          }
          if (f) break;
        }
        for (; bottom >= top; bottom--) {
          let f = false;
          const row = bottom * w * 4;
          for (let col = 0; col < w; col++) {
            if (isOpaqueAt(row + col * 4)) {
              f = true;
              break;
            }
          }
          if (f) break;
        }
        for (; left <= right; left++) {
          let f = false;
          for (let row = top; row <= bottom; row++) {
            const px = row * w * 4 + left * 4;
            if (isOpaqueAt(px)) {
              f = true;
              break;
            }
          }
          if (f) break;
        }
        for (; right >= left; right--) {
          let f = false;
          for (let row = top; row <= bottom; row++) {
            const px = row * w * 4 + right * 4;
            if (isOpaqueAt(px)) {
              f = true;
              break;
            }
          }
          if (f) break;
        }
        const cropW = Math.max(0, right - left + 1);
        const cropH = Math.max(0, bottom - top + 1);
        if (cropW <= 0 || cropH <= 0 || (cropW === w && cropH === h))
          return { changed: false };
        const out = document.createElement("canvas");
        out.width = cropW;
        out.height = cropH;
        out
          .getContext("2d")
          .drawImage(c, left, top, cropW, cropH, 0, 0, cropW, cropH);
        return {
          changed: true,
          canvas: out,
          ratioX: cropW / w,
          ratioY: cropH / h,
        };
      } catch (e) {
        console.warn("OrderDetails trim failed", e);
        return { changed: false };
      }
    };
    // Preload images
    await Promise.all(
      (picturesList || []).map((pic) => {
        if (pic && pic.__imageEl) return Promise.resolve();
        return new Promise((resolve) => {
          const el = new window.Image();
          el.crossOrigin = "anonymous";
          el.onload = () => {
            pic.__imageEl = el;
            resolve();
          };
          el.onerror = () => {
            console.warn(
              "OrderDetails: failed to load picture for sheet build",
              pic?.name || pic
            );
            resolve();
          };
          el.src = pic.file || pic.url || pic.src;
        });
      })
    );

    const PAGE_WIDTH = SHEET_WIDTH_IN * DPI;
    const PAGE_HEIGHT = SHEET_HEIGHT_IN * DPI;
    const GAP = 225;
    const MARGIN_PX = Math.round(1 * DPI); // 1-inch margins on all sides

    // Precompute trims and expand by quantity with effective sizes
    const trimInfo = new Map();
    let croppedCount = 0;
    (picturesList || []).forEach((img) => {
      const el = img.__imageEl;
      const t = trimTransparentBorder(el, 10);
      if (t.changed) croppedCount++;
      trimInfo.set(img, t);
    });

    const instances = [];
    (picturesList || []).forEach((img) => {
      const q = Math.max(1, Number(img.quantity) || 1);
      const t = trimInfo.get(img) || { changed: false, ratioX: 1, ratioY: 1 };
      for (let i = 0; i < q; i++) instances.push({ base: img, trim: t });
    });

    // Pack
    const rects = instances.map((p, i) => ({
      width: Math.round(
        (Number(p.base.widthIn) || 0) * (p.trim?.ratioX || 1) * DPI
      ),
      height: Math.round(
        (Number(p.base.heightIn) || 0) * (p.trim?.ratioY || 1) * DPI
      ),
      data: { idx: i },
    }));
    const packer = new MaxRectsPacker(
      Math.max(1, PAGE_WIDTH - 2 * MARGIN_PX),
      Math.max(1, PAGE_HEIGHT - 2 * MARGIN_PX),
      GAP,
      {
        smart: true,
        pot: false,
        allowRotation: true,
      }
    );
    packer.addArray(rects);

    // Draw sheets to canvases and trim to content height
    const dataUrls = [];
    for (const bin of packer.bins) {
      const c = document.createElement("canvas");
      c.width = PAGE_WIDTH;
      c.height = PAGE_HEIGHT;
      const x = c.getContext("2d");
      for (const r of bin.rects) {
        const p = instances[r.data.idx];
        const el =
          p.trim && p.trim.changed && p.trim.canvas
            ? p.trim.canvas
            : p.base && p.base.__imageEl;
        if (!el) continue;
        const w = r.width,
          h = r.height;
        if (r.rot) {
          x.save();
          x.translate(r.x + MARGIN_PX + h, r.y + MARGIN_PX);
          x.rotate(Math.PI / 2);
          x.drawImage(el, 0, 0, w, h);
          x.restore();
        } else {
          x.drawImage(el, r.x + MARGIN_PX, r.y + MARGIN_PX, w, h);
        }
      }
      const contentMaxY = bin.rects.reduce(
        (m, r) => Math.max(m, r.y + r.height),
        0
      );
      const maxY = Math.min(PAGE_HEIGHT, MARGIN_PX + contentMaxY + MARGIN_PX);
      const trimmed = document.createElement("canvas");
      trimmed.width = c.width;
      trimmed.height = Math.max(1, maxY);
      trimmed
        .getContext("2d")
        .drawImage(
          c,
          0,
          0,
          c.width,
          trimmed.height,
          0,
          0,
          c.width,
          trimmed.height
        );
      try {
        dataUrls.push(trimmed.toDataURL("image/png"));
      } catch (e) {
        console.warn("OrderDetails: toDataURL failed for a sheet", e);
      }
    }
    if (croppedCount > 0) {
      setCropToast(
        `Whitespace cropped automatically on ${croppedCount} image${croppedCount > 1 ? "s" : ""}.`
      );
      setTimeout(() => setCropToast(""), 2500);
    }
    return dataUrls;
  }

  async function handleBuildSheets() {
    try {
      const pics = Array.isArray(localDesigns) ? localDesigns : [];
      if (!pics.length) {
        alert("This order has no pictures to build sheets from.");
        return;
      }
      setIsBuildingSheets(true);
      setBuildUploadedCount(0);
      setBuildUploadTotal(0);

      // 1) Build sheets headlessly
      const dataUrls = await buildSheetsFromPictures(pics);
      if (!dataUrls.length) {
        alert("No sheets were generated (check picture sizes).");
        setIsBuildingSheets(false);
        return;
      }

      // 1b) Dedupe identical sheet renders so we upload each layout once
      const sheetCounts = new Map();
      const uniqueSheets = [];
      dataUrls.forEach((dataUrl) => {
        if (!dataUrl) return;
        const key = String(dataUrl);
        const nextCount = (sheetCounts.get(key) || 0) + 1;
        sheetCounts.set(key, nextCount);
        if (nextCount === 1) {
          uniqueSheets.push({ key, dataUrl });
        }
      });
      if (!uniqueSheets.length) {
        alert("Failed to prepare sheet uploads.");
        setIsBuildingSheets(false);
        return;
      }

      // 2) If existing sheets are present, delete old sheet files first (replace links)
      try {
        const storage = getStorage();
        const toDelete = new Set();
        if (Array.isArray(selectedOrder?.images)) {
          selectedOrder.images.forEach((it) => {
            const u = typeof it === "string" ? it : it?.path || it?.url;
            if (u) toDelete.add(u);
          });
        }
        if (Array.isArray(selectedOrder?.sheetGroups)) {
          selectedOrder.sheetGroups.forEach((g) => {
            const u = typeof g === "string" ? g : g?.path || g?.url;
            if (u) toDelete.add(u);
          });
        }
        if (toDelete.size) {
          await Promise.all(
            Array.from(toDelete).map(async (u) => {
              try {
                const path = isHttpUrl(u) ? extractStoragePath(u) : String(u);
                if (!path) return; // skip if not parseable
                const r = ref(storage, path);
                await deleteObject(r);
              } catch (e) {
                // Ignore not-found deletes; they mean the file was already gone or URL was stale
                if (
                  String(e?.code || e?.message || "").includes(
                    "object-not-found"
                  )
                )
                  return;
                console.warn(
                  "Delete old sheet failed:",
                  u,
                  e?.code || e?.message || e
                );
              }
            })
          );
        }
      } catch (e) {
        console.warn("Cleanup old sheets failed:", e);
      }
      // 3) Upload each unique sheet to Firebase Storage (convert to 300 DPI)
      const storage = getStorage();
      const userId = String(selectedOrder?.customerId || "anonymous");
      const orderIdSafe = String(selectedOrder?.id || "order");
      setBuildUploadTotal(uniqueSheets.length);
      const dataUrlToStored = new Map(); // key -> { url, path }
      const uploadTasks = uniqueSheets.map(async ({ dataUrl, key }, i) => {
        let dpiUrl = dataUrl;
        try {
          dpiUrl = await changeDpiDataUrl(dataUrl, 300);
        } catch (e) {
          console.warn(
            "ODS-001 changeDpiDataUrl failed, falling back to original data URL",
            e
          );
          dpiUrl = dataUrl;
        }
        const parts = typeof dpiUrl === "string" ? dpiUrl.split(",") : [];
        if (parts.length < 2) {
          console.error("ODS-002 Invalid sheet data URL encountered", {
            key,
            length: dpiUrl?.length,
          });
          throw new Error("Invalid sheet data URL");
        }
        const mimeMatch = /^data:(.*?);base64$/.exec(parts[0]);
        const contentType = mimeMatch ? mimeMatch[1] : "image/png";
        try {
          const response = await fetch(dpiUrl);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          let blob = await response.blob();
          if (!blob || blob.size === 0) {
            const decoded = decodeURIComponent(dpiUrl.split(",")[1] || "");
            blob = new Blob([decoded], { type: contentType });
          }
          const buffer = await blob.arrayBuffer();
          const bytes = new Uint8Array(buffer);
          if (!bytes.length) {
            console.error("ODS-003 Empty sheet payload generated", {
              key,
              contentType,
            });
            throw new Error("Generated sheet payload was empty");
          }
          const path = `orders/${userId}/${orderIdSafe}/${Date.now()}_${i}.png`;
          const sref = ref(storage, path);
          await uploadBytes(sref, bytes, {
            contentType: blob.type || contentType,
            cacheControl: "public, max-age=31536000, immutable",
          });
          const url = await getDownloadURL(sref);
          dataUrlToStored.set(key, { url, path });
          setBuildUploadedCount((c) => c + 1);
        } catch (uploadErr) {
          console.error("ODS-004 Failed to upload sheet blob", uploadErr);
          throw uploadErr;
        }
      });
      await Promise.all(uploadTasks);

      // 4) Build grouped metadata with quantities attached
      const groups = uniqueSheets
        .map(({ key }) => {
          const stored = dataUrlToStored.get(key);
          const quantity = Math.max(1, sheetCounts.get(key) || 1);
          if (!stored?.url) return null;
          return {
            url: stored.url,
            path: stored.path,
            quantity,
            count: quantity,
          };
        })
        .filter(Boolean);

      if (!groups.length) {
        alert("No sheets were uploaded.");
        setIsBuildingSheets(false);
        return;
      }

      // 5) Attach to order document (replace old links)
      const orderRef = doc(db, "orders", selectedOrder.id);
      await updateDoc(orderRef, {
        images: groups.map(({ url, path, quantity }) => ({
          url,
          path,
          quantity,
        })),
        sheetGroups: groups,
        lastSheetsBuiltAt: serverTimestamp(),
      });

      setCanvasEntries(
        groups.map(({ url, quantity }) => ({
          url,
          quantity,
          images: url ? [{ url, view: "Sheet", label: "Sheet" }] : [],
        }))
      );

      const totalSheets = dataUrls.length;
      alert(
        `Built ${totalSheets} sheet${totalSheets === 1 ? "" : "s"} (${groups.length} unique layout${
          groups.length === 1 ? "" : "s"
        }) and attached to order.`
      );
    } catch (e) {
      console.error("Build sheets failed:", e);
      alert("Failed to build sheets. See console for details.");
    } finally {
      setIsBuildingSheets(false);
    }
  }

  // --- helpers ---
  const toDateSafe = (ts) => {
    if (!ts) return null;
    if (typeof ts.toDate === "function") {
      try {
        return ts.toDate();
      } catch (_) {}
    }
    if (typeof ts === "number") return new Date(ts);
    if (typeof ts?.seconds === "number") return new Date(ts.seconds * 1000);
    if (typeof ts === "string") return new Date(ts);
    return null;
  };
  const fmtDateTime = (ts) => {
    if (!ts) return "—";

    // If already a string in the expected format, return as-is
    if (typeof ts === "string") {
      const s = ts.trim();
      if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(s)) return s;
      const parsed = new Date(s);
      if (isFinite(parsed)) return formatAsUtcLike(parsed);
    }

    const d = toDateSafe(ts);
    if (!d) return "—";

    return formatAsUtcLike(d);

    function pad(n, width = 2) {
      return String(n).padStart(width, "0");
    }

    // produce YYYY-MM-DD HH:mm:ss.<microseconds>+00 (uses UTC)
    function formatAsUtcLike(date) {
      const Y = date.getUTCFullYear();
      const M = pad(date.getUTCMonth() + 1);
      const D = pad(date.getUTCDate());
      const h = pad(date.getUTCHours());
      const m = pad(date.getUTCMinutes());
      const s = pad(date.getUTCSeconds());
      const ms = date.getUTCMilliseconds(); // 0-999
      const micro = String(ms).padStart(3, "0") + "000"; // expand to 6 digits
      return `${Y}-${M}-${D} ${h}:${m}:${s}.${micro}+00`;
    }
  };
  const money = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return v ?? "—";
    const amt = roundMoney(n);
    return amt.toLocaleString(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  };

  // status + payment badges
  const statusInfo = useMemo(() => {
    const raw = String((currentStatus ?? selectedOrder?.status) || "").trim();
    const norm = raw.toLowerCase().replace(/[\s_-]+/g, "");
    const key =
      norm === "in-progress" || norm === "in_progress"
        ? "inprogress"
        : norm || "pending";
    const labelMap = {
      pending: "Pending",
      downloaded: "Downloaded",
      inprogress: "In Progress",
      completed: "Completed",
      cancelled: "Cancelled",
    };
    const label = labelMap[key] || "Pending";
    const cls = `od-tag od-tag--${key in labelMap ? key : "pending"}`;
    return { key, label, cls };
  }, [selectedOrder?.status, currentStatus]);

  const paymentInfo = useMemo(() => {
    const normalize = (value) =>
      typeof value === "string"
        ? value.toLowerCase().replace(/[\s_-]+/g, "")
        : "";

    const statusKey = normalize(selectedOrder?.paymentStatus);
    if (statusKey === "paid") {
      return {
        success: true,
        label: "Paid",
        cls: "od-tag od-tag--pay-success",
      };
    }
    if (statusKey === "sent") {
      return {
        success: false,
        label: "Invoice sent",
        cls: "od-tag od-tag--pay-sent",
      };
    }
    if (statusKey === "unsent") {
      return {
        success: false,
        label: "Invoice not sent",
        cls: "od-tag od-tag--pay-unsent",
      };
    }
    if (statusKey === "unpaid") {
      return {
        success: false,
        label: "Unpaid",
        cls: "od-tag od-tag--pay-unpaid",
      };
    }

    const total = Number(
      selectedOrder?.total ?? selectedOrder?.amount ?? selectedOrder?.price ?? 0
    );
    const paid = Number(selectedOrder?.amountPaid ?? 0);
    const paymentTermsKey = normalize(
      selectedOrder?.paymentTerms || selectedOrder?.paymentType
    );
    const success =
      paymentTermsKey === "stripe"
        ? true
        : total > 0
          ? paid >= total
          : !!selectedOrder?.paidInFull || statusKey === "success";
    return {
      success,
      label: success ? "Paid" : "Payment pending",
      cls: success
        ? "od-tag od-tag--pay-success"
        : "od-tag od-tag--pay-pending",
    };
  }, [
    selectedOrder?.paymentStatus,
    selectedOrder?.total,
    selectedOrder?.amount,
    selectedOrder?.price,
    selectedOrder?.amountPaid,
    selectedOrder?.paymentTerms,
    selectedOrder?.paymentType,
    selectedOrder?.paidInFull,
  ]);

  useEffect(() => {
    if (!canvasEntries.length) return;
    let alive = true;
    const loadSizes = async () => {
      const sizes = await Promise.all(
        canvasEntries.map(
          (entry) =>
            new Promise((resolve) => {
              const img = new window.Image();
              img.onload = () =>
                resolve({
                  widthIn: roundSize(img.width / 300).toFixed(2),
                  heightIn: roundSize(img.height / 300).toFixed(2),
                });
              img.onerror = () => resolve({ widthIn: "N/A", heightIn: "N/A" });
              img.src = safeImageUrl(
                (Array.isArray(entry?.images) && entry.images[0]?.url) ||
                  entry.url ||
                  ""
              );
            })
        )
      );
      if (alive) setImageSizes(sizes);
    };
    loadSizes();
    return () => {
      alive = false;
    };
  }, [canvasEntries]);

  useEffect(() => {
    if (!Array.isArray(localDesigns) || localDesigns.length === 0) {
      setPictureSizes([]);
      return;
    }
    let alive = true;
    const loadPicSizes = async () => {
      const sizes = await Promise.all(
        localDesigns.map(
          (src) =>
            new Promise((resolve) => {
              const pic = new window.Image();
              pic.onload = () =>
                resolve({
                  widthIn: roundSize(pic.width / 300).toFixed(2),
                  heightIn: roundSize(pic.height / 300).toFixed(2),
                });
              pic.onerror = () => resolve({ widthIn: "N/A", heightIn: "N/A" });
              pic.src = safeImageUrl(src?.file || src?.url || src?.source || "");
            })
        )
      );
      if (alive) setPictureSizes(sizes);
    };
    loadPicSizes();
    return () => {
      alive = false;
    };
  }, [localDesigns]);

  // ---------- Attach Picture (Modify Order) ----------
  const [addMode, setAddMode] = useState("url"); // 'url' | 'upload'
  const [newUrl, setNewUrl] = useState("");
  const [newUrlName, setNewUrlName] = useState("");
  const [newUrlQty, setNewUrlQty] = useState(1);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState("");
  const [uploadQty, setUploadQty] = useState(1);
  const [isAttaching, setIsAttaching] = useState(false);

  const loadImgDims = (src) =>
    new Promise((resolve) => {
      try {
        const img = new window.Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve({ w: img.width || 0, h: img.height || 0 });
        img.onerror = () => resolve({ w: 0, h: 0 });
        img.src = src;
      } catch (_) {
        resolve({ w: 0, h: 0 });
      }
    });

  const sanitizeName = (name, fallback = "image.png") => {
    try {
      return (name || fallback).replace(/\s+/g, "_");
    } catch (_) {
      return fallback;
    }
  };

  const handleAttachViaUrl = async () => {
    const url = String(newUrl || "").trim();
    const qty = Math.max(1, parseInt(newUrlQty || 1, 10));
    if (!url) {
      alert("Please enter a Firebase URL");
      return;
    }
    setIsAttaching(true);
    try {
      const { w, h } = await loadImgDims(url);
      const widthIn = roundSize(w / 300);
      const heightIn = roundSize(h / 300);
      let name = newUrlName.trim();
      if (!name) {
        try {
          const storage = getStorage();
          const r = ref(storage, url);
          const md = await getMetadata(r);
          name = md?.name || md?.fullPath?.split("/").pop() || "";
        } catch (e) {
          // Fallback: derive from URL path
          name = sanitizeName(url.split("?")[0].split("/").pop() || "image");
        }
      }

      const picture = { file: url, name, widthIn, heightIn, quantity: qty };
      await updateDoc(doc(db, "orders", selectedOrder.id), {
        pictures: arrayUnion(picture),
      });
      setLocalDesigns((prev) => [...prev, picture]);
      setNewUrl("");
      setNewUrlName("");
      setNewUrlQty(1);
    } catch (err) {
      console.error("❌ Failed to attach via URL:", err);
      alert("Failed to attach picture via URL.");
    } finally {
      setIsAttaching(false);
    }
  };

  const handleAttachViaUpload = async () => {
    const file = uploadFile;
    const qty = Math.max(1, parseInt(uploadQty || 1, 10));
    if (!file) {
      alert("Please choose an image file");
      return;
    }
    setIsAttaching(true);
    try {
      const objectUrl = URL.createObjectURL(file);
      const { w, h } = await loadImgDims(objectUrl);
      URL.revokeObjectURL(objectUrl);
      const widthIn = roundSize(w / 300);
      const heightIn = roundSize(h / 300);

      const storage = getStorage();
      const uid = selectedOrder?.customerId || "unknown";
      const safeName = sanitizeName(uploadName || file.name || "image.png");
      const path = `pictures/${uid}/${Date.now()}_${safeName}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file, {
        contentType: file?.type || "image/png",
        cacheControl: "public, max-age=31536000, immutable",
      });
      const url = await getDownloadURL(storageRef);

      const picture = {
        file: url,
        name: safeName,
        widthIn,
        heightIn,
        quantity: qty,
      };
      await updateDoc(doc(db, "orders", selectedOrder.id), {
        pictures: arrayUnion(picture),
      });
      setLocalDesigns((prev) => [...prev, picture]);
      setUploadFile(null);
      setUploadName("");
      setUploadQty(1);
    } catch (err) {
      console.error("❌ Failed to upload & attach:", err);
      alert("Failed to upload and attach picture.");
    } finally {
      setIsAttaching(false);
    }
  };

  const callRefreshOrderPaymentStatus = useCallback(async (orderIds) => {
    return refreshOrderPaymentStatusCallable(orderIds);
  }, []);

  if (!selectedOrder) return null;

  const createdAt = selectedOrder.createdAtLabel;
  const imageCount = canvasEntries.length;
  const pics = designImages;
  const orderNotesSource =
    selectedOrder?.orderNotes ??
    selectedOrder?.raw?.order_notes ??
    selectedOrder?.raw?.orderNotes ??
    "";
  const orderNotesText =
    typeof orderNotesSource === "string"
      ? orderNotesSource
      : orderNotesSource != null
        ? String(orderNotesSource)
        : "";
  const hasOrderNotes = orderNotesText.trim().length > 0;
  const customerName =
    `${selectedOrder.firstName || ""} ${selectedOrder?.lastName || ""}`.trim() ||
    "Unnamed Customer";

  const onChangeStatus = async (e) => {
    const newStatus = e.target.value;
    handleStatusChange(selectedOrder.id, newStatus);
    if (String(newStatus).toLowerCase() === "completed") {
      // Server will send completion email via Cloud Function trigger
      console.log(
        "ℹ️ Status set to completed; server trigger will send completion email."
      );
    }
  };

  // view invoice handler (now under More actions)
  const viewInvoice = () => {
    const url = selectedOrder?.invoice?.pdfUrl;
    if (url) {
      const win = window.open(url, "_blank");
      if (!win || win.closed || typeof win.closed === "undefined") {
        alert("Popup blocked! Allow popups to view the invoice.");
      }
    } else {
      alert("No invoice link available for this order.");
    }
    setMenuOpen(false);
  };

  const handleChangeInvoiceId = async () => {
    const current =
      selectedOrder?.invoice?.invoiceId || selectedOrder?.invoiceId || "";
    const input = window.prompt(
      "Enter the FreshBooks invoice URL or ID for this order:",
      current
    );
    if (input === null) return;

    const invoiceId = extractInvoiceIdFromInput(input);
    if (!invoiceId) {
      alert("Could not determine a valid invoice ID from that input.");
      return;
    }
    if (invoiceId === current) {
      setMenuOpen(false);
      return;
    }

    try {
      const orderRef = doc(db, "orders", selectedOrder.id);
      const updates = {
        invoiceId,
      };
      updates["invoice.invoiceId"] = invoiceId;
      await updateDoc(orderRef, updates);
      alert(`Invoice ID updated to ${invoiceId}.`);
      setMenuOpen(false);
    } catch (err) {
      console.error("❌ Failed to update invoice ID:", err);
      alert("Failed to update invoice ID. Check the console for details.");
    }
  };

  const markOrderAsPaid = async () => {
    try {
      const oid = selectedOrder?.id;
      if (!oid) return;
      const total = Number(
        selectedOrder?.total ??
          selectedOrder?.amount ??
          selectedOrder?.price ??
          0
      );
      await updateDoc(doc(db, "orders", oid), {
        amountPaid: total,
        paymentStatus: "paid",
      });
      alert("Order marked as paid.");
    } catch (e) {
      console.error("Failed to mark as paid", e);
      alert("Failed to mark as paid.");
    }
  };

  const refreshPaymentStatus = async () => {
    const orderId = selectedOrder?.id;
    if (!orderId) {
      console.warn("⚠️ Cannot refresh payment status without an order ID.");
      return;
    }

    setIsRefreshingPaymentStatus(true);
    try {
      const payload = await callRefreshOrderPaymentStatus([orderId]);
      const results = Array.isArray(payload?.results) ? payload.results : [];

      if (!results.length) {
        console.log(
          `🔄 FreshBooks payment refresh returned no results for order ${orderId}.`,
          payload
        );
      }

      const fallbackInvoiceId =
        selectedOrder?.invoice?.invoiceId ||
        selectedOrder?.invoiceId ||
        "unknown";

      results.forEach((result) => {
        const status = result?.paymentStatus || "(no status returned)";
        const resultOrderId = result?.orderId || orderId;
        const invoiceId =
          result?.invoiceId ||
          result?.invoice_id ||
          result?.invoiceNumber ||
          fallbackInvoiceId;

        console.log(
          `📦 FreshBooks raw response for order ${resultOrderId} (invoice ${invoiceId}):`,
          result
        );

        if (result?.error) {
          console.warn(
            `⚠️ FreshBooks payment refresh error for order ${resultOrderId} (invoice ${invoiceId}):`,
            result.error
          );
        } else {
          console.log(
            `🔁 FreshBooks payment refresh for order ${resultOrderId} (invoice ${invoiceId}): ${status}`
          );
        }
      });
    } catch (err) {
      console.error(
        `❌ Failed to refresh payment status for order ${orderId}:`,
        err
      );
    } finally {
      setIsRefreshingPaymentStatus(false);
    }
  };

  // Send single-order consolidated invoice (same flow as InvoicesPanel but for one order)
  const handleSendInvoice = async () => {
    try {
      const pictures = designImages;
      if (pictures.length === 0) {
        alert("This order has no pictures/line items to invoice.");
        return;
      }

      const customerId =
        customerData?.freshbooksClientId?.id ||
        customerData?.freshbooksClientId ||
        null;
      if (!customerId) {
        alert(
          "This customer does not have a FreshBooks client ID. Cannot create invoice."
        );
        return;
      }

      setIsCreatingInvoice(true);

      const createInvoiceFn = httpsCallable(
        firebaseFunctions,
        "createConsolidatedInvoice"
      );
      const payload = {
        pictures,
        invoiceNumber: undefined,
        poNumber: "",
        emailRecipients: [],
        customerId,
        userId: selectedOrder?.customerId,
        rate: customerData?.rate ?? 0,
        paymentType: "Net 30",
        orderIds: selectedOrder?.id ? [selectedOrder.id] : [],
      };

      const result = await createInvoiceFn(payload);
      const { invoiceId, pdfUrl, paymentStatus } = result?.data || {};
      console.log("✅ Single-order invoice created:", {
        invoiceId,
        pdfUrl,
        paymentStatus,
      });

      if (pdfUrl) {
        alert(`Invoice created! You can download it here:\n${pdfUrl}`);
        const win = window.open(pdfUrl, "_blank");
        if (!win || win.closed || typeof win.closed === "undefined") {
          console.warn("Popup blocked; showing URL in alert instead.");
        }
      } else {
        alert(`Invoice created! ID: ${invoiceId || "(unknown)"}`);
      }

      // Also update the stackedOrders doc for this user: remove this order and set lastCleared
      try {
        const stackedRef = doc(db, "stackedOrders", selectedOrder.customerId);
        await updateDoc(stackedRef, {
          orders: arrayRemove(selectedOrder.id),
          lastCleared: serverTimestamp(),
        });
        console.log(
          "✅ Updated stackedOrders for user",
          selectedOrder.customerId
        );
      } catch (updateErr) {
        console.error("❌ Failed to update stackedOrders:", updateErr);
      }

      try {
        await callRefreshOrderPaymentStatus(
          selectedOrder?.id ? [selectedOrder.id] : []
        );
        console.log("🔄 Payment status refreshed for order", selectedOrder?.id);
      } catch (refreshErr) {
        console.warn(
          "⚠️ Failed to refresh payment status after single-order invoice:",
          refreshErr
        );
      }
    } catch (err) {
      console.error("❌ Error creating invoice:", err);
      alert("Failed to create invoice. See console for details.");
    } finally {
      setIsCreatingInvoice(false);
    }
  };

  // Helper: reliably download Firebase Storage URLs or data URLs
  const downloadFile = async (src, filename = "download.png") => {
    try {
      if (!src) return;
      if (!isAllowedDownloadSrc(src)) {
        console.warn("Blocked download for unsafe URL:", src);
        alert("Blocked an unsafe download URL.");
        return;
      }
      // If it's a data URL, skip fetch and use it directly
      if (typeof src === "string" && src.startsWith("data:")) {
        const a = document.createElement("a");
        a.href = src;
        a.download = filename;
        a.rel = "noopener noreferrer";
        a.target = "_blank";
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }

      // Otherwise fetch as a blob (works with Firebase download URLs)
      const res = await fetch(src, { mode: "cors", referrerPolicy: "no-referrer" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
      // release object URL shortly after click
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      console.error("Download failed:", err);
      alert("Couldn't download file. See console for details.");
    }
  };

  return (
    <div className="od">
      {!!cropToast && (
        <div
          style={{
            position: "fixed",
            bottom: 20,
            left: "50%",
            transform: "translateX(-50%)",
            background: "#1f2937",
            color: "#fff",
            padding: "6px 12px",
            borderRadius: 12,
            boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
            zIndex: 5000,
            fontFamily: "Anek Latin",
          }}
        >
          {cropToast}
        </div>
      )}
      {/* header */}
      <div className="od-head">
        <div className="od-title">
          <button className="op-btn op-btn--ghost od-back" onClick={onBack}>
            ← Back
          </button>
          <h2>Order ID: {selectedOrder.id}</h2>
          <div className="od-sub">Submitted {createdAt}</div>
          <div className="od-badges">
            <span className={statusInfo.cls}>{statusInfo.label}</span>
          </div>
        </div>
        <div className="od-head-actions">
          <div className="od-menu">
            <button className="op-btn" onClick={() => setMenuOpen((v) => !v)}>
              More actions ▾
            </button>

            {menuOpen && (
              <div className="od-menu__list">
                <button className="od-menu__item" onClick={viewInvoice}>
                  View invoice
                </button>
                <button
                  className="od-menu__item"
                  onClick={handleChangeInvoiceId}
                >
                  Set invoice ID
                </button>
                <button
                  className="od-menu__item"
                  onClick={() => setMenuOpen(false)}
                >
                  Create shipping label
                </button>
                <button
                  className="od-menu__item"
                  onClick={() => setMenuOpen(false)}
                >
                  Duplicate
                </button>
              </div>
            )}
          </div>
          <div className="od-menu">
            <button
              className="op-btn"
              onClick={() => {
                setStatusOpen((v) => !v);
                setMenuOpen(false);
              }}
            >
              Set Status ▾
            </button>
            {statusOpen && (
              <div className="od-menu__list">
                <button
                  className="od-menu__item"
                  onClick={() => {
                    handleStatusChange(selectedOrder.id, "Pending");
                    setStatusOpen(false);
                  }}
                >
                  Pending
                </button>
                <button
                  className="od-menu__item"
                  onClick={() => {
                    handleStatusChange(selectedOrder.id, "Downloaded");
                    setStatusOpen(false);
                  }}
                >
                  Downloaded
                </button>
                <button
                  className="od-menu__item"
                  onClick={() => {
                    handleStatusChange(selectedOrder.id, "In Progress");
                    setStatusOpen(false);
                  }}
                >
                  In Progress
                </button>
                <button
                  className="od-menu__item"
                  onClick={() => {
                    handleStatusChange(selectedOrder.id, "Completed");
                    setStatusOpen(false);
                  }}
                >
                  Completed
                </button>
                <button
                  className="od-menu__item"
                  onClick={() => {
                    handleStatusChange(selectedOrder.id, "Cancelled");
                    setStatusOpen(false);
                  }}
                >
                  Cancelled
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* grid: left (items/summary/timeline) + right (customer/addresses) */}
      <div className="od-grid">
        <div className="od-col od-col--left">
          {/* Order Items */}
          <section className="od-card">
            <div className="od-card__header">
              <div className="od-card__title">Order Items</div>
              <div className="od-head-actions">
                <button
                  className="op-btn"
                  onClick={() => setIsEditing(!isEditing)}
                >
                  Edit
                </button>
                <button
                  className="op-btn op-btn--primary"
                  onClick={handleBuildSheets}
                  disabled={isBuildingSheets}
                  title="Build arranged sheets from pictures and attach to order"
                >
                  {isBuildingSheets
                    ? `Building… (${buildUploadedCount}/${buildUploadTotal})`
                    : "Build Sheets"}
                </button>
              </div>
            </div>

            {/* Thumbs */}
            <div className="od-thumbs">
              {canvasEntries.map((entry, i) => {
                const images =
                  Array.isArray(entry?.images) && entry.images.length
                    ? entry.images
                    : [
                        {
                          url: entry.url,
                          view: entry.view || "Preview",
                          label: entry.view || "Preview",
                        },
                      ];
                const primaryImage = images[0];
                const src = primaryImage?.url || entry.url;
                const qty = entry?.quantity;
                const size = entry?.size;
                const color = entry?.color;
                const price = entry?.price;
                const title = entry?.name || `Canvas ${i + 1}`;
                const stableKey = entry?.cartItemId || entry?.url || `entry-${i}`;
                return (
                  <div key={stableKey} className="od-thumb">
                    <div
                      className="od-thumb__images"
                      style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
                    >
                      {images.map((imgEntry, imgIndex) => (
                        <button
                          key={`${stableKey}-${imgEntry.view || imgIndex}-${imgEntry.url || "u"}`}
                          type="button"
                          onClick={() => setEnlargedImage(imgEntry.url)}
                          style={{
                            border: "none",
                            background: "transparent",
                            padding: 0,
                            cursor: "zoom-in",
                            position: "relative",
                          }}
                        >
                          <img
                            src={safeImageUrl(imgEntry.url)}
                            alt={`${title} ${imgEntry.view || ""}`.trim()}
                            loading="lazy"
                            decoding="async"
                            style={{
                              maxWidth: images.length > 1 ? 140 : 160,
                              maxHeight: 140,
                              width: "100%",
                              height: "auto",
                              borderRadius: 8,
                              border: "1px solid var(--op-border)",
                            }}
                          />
                          {imgEntry.view && (
                            <span
                              style={{
                                position: "absolute",
                                bottom: 6,
                                left: 6,
                                background: "rgba(0,0,0,0.65)",
                                color: "#fff",
                                fontSize: 11,
                                padding: "2px 6px",
                                borderRadius: 6,
                              }}
                            >
                              {imgEntry.view}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                    <div className="od-thumb__meta">
                      <div className="od-thumb__title">{title}</div>
                      <div className="od-thumb__sub">Color: {color}</div>
                      <div className="od-thumb__sub">Size: {size}</div>
                      <div className="od-thumb__sub">Price: ${price}</div>
                      <div className="od-thumb__sub">Quantity: {qty}</div>
                    </div>
                    <div className="od-thumb__actions">
                      <button
                        className="op-btn"
                        disabled={!(
                          (Array.isArray(entry?.designDownloads) && entry.designDownloads.length) ||
                          !!src
                        )}
                        onClick={() => {
                          const downloads = Array.isArray(entry?.designDownloads)
                            ? entry.designDownloads
                            : [];
                          if (!downloads.length) {
                            // Fallback to primary src
                            if (src) downloadFile(src, `sheet_${i + 1}.png`);
                            return;
                          }
                          downloads.forEach((d, idx) => {
                            const label = (d?.label || `part-${idx + 1}`).toLowerCase();
                            const safeTitle = String(title || `design-${i + 1}`)
                              .trim()
                              .replace(/\s+/g, "_")
                              .toLowerCase();
                            const filename = `${safeTitle}_${label}.png`;
                            downloadFile(d.url, filename);
                          });
                        }}
                      >
                        Download design
                      </button>
                    </div>
                  </div>
                );
              })}
              <div style={{ borderBottom: "1px dashed var(--op-border)" }} />

              {(localDesigns || []).map((pic, i) => {
                const src = pic?.file;
                const fallbackSize = pictureSizes[i];
                const picKey = pic?.file || pic?.name || `picture-${i}`;
                return (
                  <div key={picKey} className="od-thumb">
                    <img
                      src={safeImageUrl(src)}
                      alt={`${pic.name} ${i + 1}`}
                      loading="lazy"
                      decoding="async"
                      style={{ cursor: src ? "zoom-in" : "default" }}
                      onClick={() => src && setEnlargedImage(src)}
                    />
                    <div className="od-thumb__meta">
                      <div className="od-thumb__title">{pic.name}</div>
                      <div className="od-thumb__sub">
                        Quantity: {pic.quantity}
                      </div>
                      <div className="od-thumb__sub">
                        {pic.widthIn ?? fallbackSize?.widthIn ?? "N/A"}" ×{" "}
                        {pic.heightIn ?? fallbackSize?.heightIn ?? "N/A"}"
                      </div>
                    </div>
                    <div className="od-thumb__actions">
                      <button
                        className="op-btn"
                        disabled={!src}
                        onClick={() =>
                          src &&
                          downloadFile(src, pic?.name || `picture_${i + 1}.png`)
                        }
                      >
                        Download
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="od-actions od-actions--row"></div>
            {isEditing && (
              <>
                <div className="od-card__header">
                  <div className="od-card__title">Modify Order</div>
                </div>
                <div className="od-rows">
                  <div className="od-row">
                    <span>Add method</span>
                    <div>
                      <label style={{ marginRight: 12 }}>
                        <input
                          type="radio"
                          checked={addMode === "url"}
                          onChange={() => setAddMode("url")}
                        />{" "}
                        Attach by Firebase URL
                      </label>
                      <label>
                        <input
                          type="radio"
                          checked={addMode === "upload"}
                          onChange={() => setAddMode("upload")}
                        />{" "}
                        Upload file to Firebase
                      </label>
                    </div>
                  </div>

                  {addMode === "url" ? (
                    <>
                      <div className="od-row">
                        <span>Firebase URL</span>
                        <input
                          type="text"
                          value={newUrl}
                          onChange={(e) => setNewUrl(e.target.value)}
                          placeholder="https://firebasestorage.googleapis.com/..."
                          style={{ width: "100%" }}
                        />
                      </div>
                      <div className="od-row">
                        <span>Name (optional)</span>
                        <input
                          type="text"
                          value={newUrlName}
                          onChange={(e) => setNewUrlName(e.target.value)}
                          placeholder="Display name"
                        />
                      </div>
                      <div className="od-row">
                        <span>Quantity</span>
                        <input
                          type="number"
                          min={1}
                          value={newUrlQty}
                          onChange={(e) => setNewUrlQty(e.target.value)}
                          style={{ width: 80 }}
                        />
                      </div>
                      <div className="od-actions od-actions--row">
                        <button
                          className="op-btn op-btn--primary"
                          onClick={handleAttachViaUrl}
                          disabled={isAttaching}
                        >
                          {isAttaching ? "Attaching…" : "Attach picture"}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="od-row">
                        <span>Image file</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) =>
                            setUploadFile(e.target.files?.[0] || null)
                          }
                        />
                      </div>
                      <div className="od-row">
                        <span>Name</span>
                        <input
                          type="text"
                          value={uploadName}
                          onChange={(e) => setUploadName(e.target.value)}
                          placeholder="e.g., design.png"
                        />
                      </div>
                      <div className="od-row">
                        <span>Quantity</span>
                        <input
                          type="number"
                          min={1}
                          value={uploadQty}
                          onChange={(e) => setUploadQty(e.target.value)}
                          style={{ width: 80 }}
                        />
                      </div>
                      <div className="od-actions od-actions--row">
                        <button
                          className="op-btn op-btn--primary"
                          onClick={handleAttachViaUpload}
                          disabled={isAttaching}
                        >
                          {isAttaching ? "Uploading…" : "Upload & attach"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
            <div className="od-actions od-actions--row">
              <button className="op-btn">Fulfill item</button>
              <button className="op-btn">Create shipping label</button>
            </div>
          </section>

          {/* Order Summary */}
          <section className="od-card">
            <div className="od-card__header">
              <div className="od-card__title">Order Summary</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className={paymentInfo.cls}>
                  Payment {paymentInfo.success ? "received" : "pending"}
                </span>
                {!paymentInfo.success && (
                  <button
                    className="op-btn op-btn--primary"
                    onClick={markOrderAsPaid}
                  >
                    Mark Paid
                  </button>
                )}
              </div>
            </div>
            <div className="od-rows">
              <div className="od-row">
                <span>Subtotal</span>
                {money(selectedOrder?.subtotal ?? selectedOrder?.total ?? 0)}
              </div>
              {customerData?.stripeFee === true ? (
                <div className="od-row">
                  <span>Stripe Fee</span>

                  {money(selectedOrder?.total - selectedOrder?.subtotal)}
                </div>
              ) : null}
              <div className="od-row od-row--total">
                <span>Total</span>
                {money(selectedOrder?.total ?? 0)}
              </div>
            </div>
            <div className="od-actions od-actions--row">
              <button
                className="op-btn"
                onClick={handleSendInvoice}
                disabled={isCreatingInvoice}
              >
                {isCreatingInvoice ? "Creating..." : "Send invoice"}
              </button>
              <button className="op-btn">Collect payment</button>
              <button
                className="op-btn"
                onClick={refreshPaymentStatus}
                disabled={isRefreshingPaymentStatus}
              >
                {isRefreshingPaymentStatus
                  ? "Refreshing..."
                  : "Refresh payment status"}
              </button>
            </div>
          </section>

          <section className="od-card">
            <div className="od-card__header">
              <div className="od-card__title">Order Notes</div>
            </div>
            {hasOrderNotes ? (
              <div
                style={{
                  padding: "12px 16px",
                  background: "var(--op-panel)",
                  borderRadius: 12,
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.5,
                  fontFamily: "Anek Latin",
                }}
              >
                {orderNotesText}
              </div>
            ) : (
              <div
                style={{
                  padding: "12px 16px",
                  color: "var(--op-muted)",
                  fontStyle: "italic",
                }}
              >
                No order notes provided.
              </div>
            )}
          </section>

          {/* Timeline */}
          <section className="od-card">
            <div className="od-card__header">
              <div className="od-card__title">Timeline</div>
            </div>
            <p className="od-muted">Canvasses</p>
            <div className="od-comment">
              <input placeholder="Leave a comment…" />
              <button className="op-btn">Post</button>
            </div>
          </section>
        </div>

        {/* Right column */}
        <aside className="od-col od-col--right">
          <section className="od-card">
            <div className="od-card__header">
              <div className="od-card__title">Notes</div>
            </div>
            <ul className="od-note-list">
              {internalNotes && internalNotes.length > 0 ? (
                internalNotes.map((n, i) => (
                  <li key={i} className="od-note">
                    <div className="od-note__text">{n.text}</div>
                    <div className="od-note__meta">
                      {n.authorName || n.authorEmail || "Staff"} •{" "}
                      {n.createdAt
                        ? new Date(n.createdAt).toLocaleString()
                        : ""}
                    </div>
                  </li>
                ))
              ) : (
                <li className="od-note od-note--empty">
                  No internal notes yet.
                </li>
              )}
            </ul>
            <div className="od-noteform">
              <textarea
                placeholder="Add a private note for staff…"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                rows={3}
              />
              <button
                className="op-btn op-btn--primary"
                onClick={handleAddInternalNote}
              >
                Add note
              </button>
            </div>
          </section>

          <section className="od-card">
            <div className="od-card__header">
              <div className="od-card__title">Customer</div>
            </div>
            <div className="od-list">
              <div className="od-list__item">
                <div className="od-avatar">
                  {(customerName || "A").slice(0, 2).toUpperCase()}
                </div>
                <div className="od-list__meta">
                  <div className="od-list__title">{customerName}</div>
                  <div className="od-list__sub">
                    {customerData?.ordersCount
                      ? `${customerData.ordersCount} Orders`
                      : "1 Order"}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="od-card">
            <div className="od-card__header">
              <div className="od-card__title">Contact Information</div>
            </div>
            <div className="od-rows">
              <div className="od-row">
                <EmailOutlined />

                {selectedOrder?.email ? (
                  <a href={`mailto:${selectedOrder.email}`}>
                    {selectedOrder.email}
                  </a>
                ) : (
                  "N/A"
                )}
              </div>
              <div className="od-row">
                <PhoneOutlined />
                {selectedOrder?.phone || "No phone number"}
              </div>
            </div>
          </section>

          <section className="od-card">
            <div className="od-card__header">
              <div className="od-card__title">Shipping address</div>
            </div>
            <div className="od-address">
              <div>{customerName}</div>
              <div>
                {selectedOrder?.shippingAddress?.line1 ||
                  selectedOrder?.address1 ||
                  "—"}
              </div>
              <div>
                {selectedOrder?.shippingAddress?.city ||
                  selectedOrder?.city ||
                  ""}{" "}
                {selectedOrder?.shippingAddress?.zip ||
                  selectedOrder?.zip ||
                  ""}
              </div>
              <div>
                {selectedOrder?.shippingAddress?.country ||
                  selectedOrder?.country ||
                  "United States"}
              </div>
              {selectedOrder?.phone && <div>{selectedOrder.phone}</div>}
            </div>
          </section>

          <section className="od-card">
            <div className="od-card__header">
              <div className="od-card__title">Billing address</div>
            </div>
            <div className="od-muted">Same as shipping address</div>
          </section>
        </aside>
      </div>

      {/* Preview Overlay (contained, scaled for easier viewing) */}
      {enlargedImage && (
        <div
          className="op-modal"
          onClick={() => setEnlargedImage(null)}
          style={{
            position: "fixed",
            inset: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 4000,
            padding: 12,
          }}
        >
          <div
            className="op-modal__content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 18px 60px rgba(0,0,0,0.45)",
              padding: 12,
              maxWidth: "90vw",
              maxHeight: "90vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              overflow: "auto",
            }}
          >
            {/* close */}
            <button
              onClick={() => setEnlargedImage(null)}
              aria-label="Close preview"
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                border: "none",
                background: "rgba(0,0,0,0.6)",
                color: "#fff",
                borderRadius: 8,
                padding: "6px 10px",
                cursor: "pointer",
              }}
            >
              Close ✕
            </button>
            <img
              src={safeImageUrl(enlargedImage)}
              alt="Preview"
              style={{
                display: "block",
                maxWidth: "85vw",
                maxHeight: "80vh",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                backgroundColor: "#ababab",
                backgroundImage:
                  "linear-gradient(45deg, #ccc 25%, transparent 25%),\
                   linear-gradient(-45deg, #ccc 25%, transparent 25%),\
                   linear-gradient(45deg, transparent 75%, #ccc 75%),\
                   linear-gradient(-45deg, transparent 75%, #ccc 75%)",
                backgroundSize: "20px 20px",
                backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
                border: "4px solid #fff",
                borderRadius: 8,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
